import { useIsMobile } from '../hooks/useIsMobile';
import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Button, TextField,
  Select, MenuItem, FormControl, InputLabel, Alert,
  Table, TableHead, TableBody, TableRow, TableCell,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, InputAdornment
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SearchIcon from '@mui/icons-material/Search';
import Sidebar from '../components/Sidebar';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';


const SIDEBAR_WIDTH = 230;

const ABSENCE_TYPES = [
  { value: 'sick_leave', label: 'Sick Leave' },
  { value: 'annual_leave', label: 'Annual Leave' },
  { value: 'official_duty', label: 'Official Duty' },
  { value: 'compassionate_leave', label: 'Compassionate Leave' },
  { value: 'casual_leave', label: 'Casual Leave' },
  { value: 'maternity_leave', label: 'Maternity Leave' },
  { value: 'paternity_leave', label: 'Paternity Leave' },
  { value: 'study_leave', label: 'Study Leave' },
  { value: 'other', label: 'Other' },
];

const STATUS_COLORS = {
  pending: { bg: '#faeeda', color: '#633806' },
  dept_approved: { bg: '#e6f1fb', color: '#0c447c' },
  approved: { bg: '#eaf3de', color: '#27500a' },
  rejected: { bg: '#fcebeb', color: '#791f1f' },
};

function StatusChip({ status, label }) {
  const colors = STATUS_COLORS[status] || { bg: '#f1efe8', color: '#444' };
  return (
    <Chip
      label={label}
      size="small"
      sx={{ background: colors.bg, color: colors.color, fontSize: 11, fontWeight: 500 }}
    />
  );
}

function AbsenceFilterBar({
  search, setSearch,
  filterStatus, setFilterStatus,
  filterType, setFilterType,
  filterDateFrom, setFilterDateFrom,
  filterDateTo, setFilterDateTo,
  onClear, hasActiveFilters,
  showEmployeeSearch = false,
}) {
  return (
    <Box sx={{ p: 2, background: '#fafcfa', borderBottom: '0.5px solid #e8ede8' }}>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {showEmployeeSearch && (
          <TextField
            placeholder="Search by name, department..."
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: '#aaa' }} />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 220 }}
          />
        )}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <MenuItem value="">All statuses</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="dept_approved">Dept. Approved</MenuItem>
            <MenuItem value="approved">Fully Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Absence type</InputLabel>
          <Select
            label="Absence type"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <MenuItem value="">All types</MenuItem>
            {ABSENCE_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="From date" type="date" size="small"
          value={filterDateFrom}
          onChange={(e) => setFilterDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        <TextField
          label="To date" type="date" size="small"
          value={filterDateTo}
          onChange={(e) => setFilterDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        {hasActiveFilters && (
          <Button size="small" onClick={onClear} sx={{ color: '#888' }}>
            Clear filters
          </Button>
        )}
      </Box>
    </Box>
  );
}

function AbsenceTable({ records, showEmployee, showActions, onAction, actionableStatuses = [] }) {
  return (
    <Box sx={{ overflowX: 'auto' }}>
  <Table size="small" sx={{ minWidth: 600 }}>
      <TableHead>
        <TableRow>
          {showEmployee && <TableCell>Employee</TableCell>}
          <TableCell>Type</TableCell>
          <TableCell>From</TableCell>
          <TableCell>To</TableCell>
          <TableCell>Days</TableCell>
          <TableCell>Status</TableCell>
          {showActions && <TableCell>Actions</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {records.map((r) => (
          <TableRow key={r.id} sx={{ '&:last-child td': { border: 0 } }}>
            {showEmployee && (
              <TableCell>
                <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                  {r.employee_detail?.first_name} {r.employee_detail?.last_name}
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#888' }}>
                  {r.employee_detail?.department}
                </Typography>
              </TableCell>
            )}
            <TableCell>{r.absence_type_display}</TableCell>
            <TableCell>{r.date_from}</TableCell>
            <TableCell>{r.date_to}</TableCell>
            <TableCell>{r.duration_days}</TableCell>
            <TableCell>
              <StatusChip status={r.status} label={r.status_display} />
            </TableCell>
            {showActions && (
              <TableCell>
                {actionableStatuses.includes(r.status) ? (
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{
                      fontSize: 11, py: 0.25, borderRadius: 1.5,
                      borderColor: '#1a4d2e', color: '#1a4d2e',
                    }}
                    onClick={() => onAction(r)}
                  >
                    Review
                  </Button>
                ) : (
                  <Typography sx={{ fontSize: 11, color: '#aaa' }}>—</Typography>
                )}
              </TableCell>
            )}
          </TableRow>
        ))}
        {records.length === 0 && (
          <TableRow>
            <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#aaa', fontSize: 13 }}>
              No absence requests found
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
    </Box>
  );
}

export default function AbsencePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isDeptHead = user?.role === 'department_head';
  const fileInputRef = useRef();
  const isMobile = useIsMobile();

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [myRequests, setMyRequests] = useState([]);
  const [deptRequests, setDeptRequests] = useState([]);
  const [adminRequests, setAdminRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [openSubmit, setOpenSubmit] = useState(false);
  const [actionDialog, setActionDialog] = useState({ open: false, request: null, role: '' });
  const [actionComment, setActionComment] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [form, setForm] = useState({
    absence_type: '', date_from: '', date_to: '', reason: '',
  });

  const today = new Date().toLocaleDateString('en-GB', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const myRes = await api.get('/absence/my/');
      setMyRequests(Array.isArray(myRes.data) ? myRes.data : myRes.data.results || []);
      if (isDeptHead || isAdmin) {
        const deptRes = await api.get('/absence/department/');
        setDeptRequests(Array.isArray(deptRes.data) ? deptRes.data : []);
      }
      if (isAdmin) {
        const adminRes = await api.get('/absence/admin/');
        setAdminRequests(Array.isArray(adminRes.data) ? adminRes.data : []);
      }
    } catch {
      setMsg({ type: 'error', text: 'Failed to load absence requests.' });
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (records) => {
    let result = [...records];
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(r =>
        r.employee_detail?.first_name?.toLowerCase().includes(q) ||
        r.employee_detail?.last_name?.toLowerCase().includes(q) ||
        r.employee_detail?.username?.toLowerCase().includes(q) ||
        r.employee_detail?.department?.toLowerCase().includes(q) ||
        r.reason?.toLowerCase().includes(q)
      );
    }
    if (filterStatus) result = result.filter(r => r.status === filterStatus);
    if (filterType) result = result.filter(r => r.absence_type === filterType);
    if (filterDateFrom) result = result.filter(r => r.date_from >= filterDateFrom);
    if (filterDateTo) result = result.filter(r => r.date_to <= filterDateTo);
    return result;
  };

  const clearFilters = () => {
    setSearch('');
    setFilterStatus('');
    setFilterType('');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const hasActiveFilters = search || filterStatus || filterType || filterDateFrom || filterDateTo;

  const handleSubmit = async () => {
    if (!form.absence_type || !form.date_from || !form.date_to || !form.reason) {
      setMsg({ type: 'error', text: 'Please fill in all required fields.' });
      return;
    }
    if (new Date(form.date_to) < new Date(form.date_from)) {
      setMsg({ type: 'error', text: 'End date cannot be before start date.' });
      return;
    }
    setSubmitLoading(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => formData.append(k, v));
      if (selectedFile) formData.append('supporting_document', selectedFile);
      await api.post('/absence/submit/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setMsg({ type: 'success', text: 'Absence request submitted successfully.' });
      setOpenSubmit(false);
      setForm({ absence_type: '', date_from: '', date_to: '', reason: '' });
      setSelectedFile(null);
      fetchAll();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.reason || 'Submission failed.' });
    } finally {
      setSubmitLoading(false);
    }
  };

  const openAction = (request, role) => {
    setActionDialog({ open: true, request, role });
    setActionComment('');
  };

  const handleAction = async (action) => {
    const { request, role } = actionDialog;
    const endpoint = role === 'admin'
      ? `/absence/admin/${request.id}/action/`
      : `/absence/department/${request.id}/action/`;
    try {
      await api.post(endpoint, { action, comment: actionComment });
      setMsg({
        type: 'success',
        text: action === 'approve' ? 'Request approved.' : 'Request rejected.'
      });
      setActionDialog({ open: false, request: null, role: '' });
      fetchAll();
    } catch {
      setMsg({ type: 'error', text: 'Action failed. Please try again.' });
    }
  };

  const approvedAbsences = myRequests.filter(r => r.status === 'approved');

  const filterBarProps = {
    search, setSearch,
    filterStatus, setFilterStatus,
    filterType, setFilterType,
    filterDateFrom, setFilterDateFrom,
    filterDateTo, setFilterDateTo,
    onClear: clearFilters,
    hasActiveFilters,
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: '#f4f6f4' }}>
      <Sidebar />
      <Box sx={{ ml: isMobile ? 0 : `${SIDEBAR_WIDTH}px`, p: isMobile ? 2 : 3, pt: isMobile ? 7 : 3, minHeight: '100vh', flex: 1, minWidth: 0, overflowX: 'hidden' }}>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 500 }}>Absence Requests</Typography>
            <Typography sx={{ color: '#888', fontSize: 13, mt: 0.5 }}>{today}</Typography>
          </Box>
          {!isAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenSubmit(true)}
              sx={{ background: '#1a4d2e', '&:hover': { background: '#2d7a4f' } }}
            >
              New Request
            </Button>
          )}
        </Box>

        {msg.text && (
          <Alert severity={msg.type} sx={{ mb: 2, borderRadius: 2 }} onClose={() => setMsg({ type: '', text: '' })}>
            {msg.text}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
            <CircularProgress sx={{ color: '#1a4d2e' }} />
          </Box>
        ) : (
          <>
            {/* Approved absences banner */}
            {!isAdmin && !isDeptHead && approvedAbsences.length > 0 && (
              <Card sx={{ mb: 3, border: '0.5px solid #2d7a4f', background: '#f0f8f0' }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CheckCircleIcon sx={{ color: '#2d7a4f', fontSize: 18 }} />
                    <Typography sx={{ fontWeight: 500, fontSize: 14, color: '#1a4d2e' }}>
                      Approved absences
                    </Typography>
                  </Box>
                  {approvedAbsences.map((a) => (
                    <Box key={a.id} sx={{ fontSize: 13, color: '#444', mb: 0.5 }}>
                      {a.absence_type_display} — {a.date_from} to {a.date_to} ({a.duration_days} day{a.duration_days !== 1 ? 's' : ''})
                    </Box>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* My requests — employees only */}
            {!isAdmin && !isDeptHead && (
              <Card sx={{ mb: 3 }}>
                <Box sx={{ px: 2.5, py: 1.5, borderBottom: '0.5px solid #e8ede8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>My absence requests</Typography>
                  <Typography sx={{ fontSize: 12, color: '#888' }}>{applyFilters(myRequests).length} of {myRequests.length}</Typography>
                </Box>
                <AbsenceFilterBar {...filterBarProps} showEmployeeSearch={false} />
                <AbsenceTable records={applyFilters(myRequests)} showEmployee={false} />
              </Card>
            )}

            {/* Dept head view */}
            {isDeptHead && (
              <Card sx={{ mb: 3 }}>
                <Box sx={{ px: 2.5, py: 1.5, borderBottom: '0.5px solid #e8ede8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                    {user.department} department requests
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#888' }}>
                    {applyFilters(deptRequests).filter(r => r.status === 'pending').length} pending
                  </Typography>
                </Box>
                <AbsenceFilterBar {...filterBarProps} showEmployeeSearch={true} />
                <AbsenceTable
                  records={applyFilters(deptRequests)}
                  showEmployee
                  showActions
                  actionRole="dept_head"
                  onAction={(r) => openAction(r, 'dept_head')}
                  actionableStatuses={['pending']}
                />
              </Card>
            )}

            {/* Admin view */}
            {isAdmin && (
              <Card>
                <Box sx={{ px: 2.5, py: 1.5, borderBottom: '0.5px solid #e8ede8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>All absence requests</Typography>
                  <Typography sx={{ fontSize: 12, color: '#888' }}>
                    {applyFilters(adminRequests).filter(r => r.status === 'dept_approved').length} awaiting final approval
                  </Typography>
                </Box>
                <AbsenceFilterBar {...filterBarProps} showEmployeeSearch={true} />
                <AbsenceTable
                  records={applyFilters(adminRequests)}
                  showEmployee
                  showActions
                  actionRole="admin"
                  onAction={(r) => openAction(r, 'admin')}
                  actionableStatuses={['dept_approved']}
                />
              </Card>
            )}
          </>
        )}

        {/* Submit dialog */}
        <Dialog open={openSubmit} onClose={() => setOpenSubmit(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
          <DialogTitle sx={{ fontWeight: 500 }}>New Absence Request</DialogTitle>
          <DialogContent>
            <FormControl fullWidth margin="dense">
              <InputLabel>Absence type</InputLabel>
              <Select
                label="Absence type"
                value={form.absence_type}
                onChange={(e) => setForm({ ...form, absence_type: e.target.value })}
              >
                {ABSENCE_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, mt: 0.5 }}>
              <TextField
                label="From date" type="date" margin="dense"
                InputLabelProps={{ shrink: true }}
                value={form.date_from}
                onChange={(e) => setForm({ ...form, date_from: e.target.value })}
              />
              <TextField
                label="To date" type="date" margin="dense"
                InputLabelProps={{ shrink: true }}
                value={form.date_to}
                onChange={(e) => setForm({ ...form, date_to: e.target.value })}
              />
            </Box>
            <TextField
              label="Reason" fullWidth margin="dense" multiline rows={3}
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            />
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              onChange={(e) => setSelectedFile(e.target.files[0])}
            />
            <Box sx={{ mt: 1.5 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => fileInputRef.current.click()}
                sx={{ borderColor: '#2d7a4f', color: '#1a4d2e', borderRadius: 2 }}
              >
                {selectedFile ? selectedFile.name : 'Upload supporting document (optional)'}
              </Button>
              {selectedFile && (
                <Typography sx={{ fontSize: 11, color: '#888', mt: 0.5 }}>
                  {selectedFile.name} — {(selectedFile.size / 1024).toFixed(1)} KB
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setOpenSubmit(false)} sx={{ color: '#888' }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitLoading}
              sx={{ background: '#1a4d2e', '&:hover': { background: '#2d7a4f' } }}
            >
              {submitLoading ? <CircularProgress size={18} color="inherit" /> : 'Submit Request'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Action dialog */}
        <Dialog
          open={actionDialog.open}
          onClose={() => setActionDialog({ open: false, request: null, role: '' })}
          maxWidth="xs"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ fontWeight: 500 }}>Review Absence Request</DialogTitle>
          <DialogContent>
            {actionDialog.request && (
              <Box>
                <Box sx={{ mb: 2, p: 1.5, background: '#fafcfa', borderRadius: 2, border: '0.5px solid #e0e8e0' }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                    {actionDialog.request.employee_detail?.first_name} {actionDialog.request.employee_detail?.last_name}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#888' }}>
                    {actionDialog.request.absence_type_display}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#888' }}>
                    {actionDialog.request.date_from} → {actionDialog.request.date_to} ({actionDialog.request.duration_days} days)
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#555', mt: 0.5 }}>
                    {actionDialog.request.reason}
                  </Typography>
                </Box>
                <TextField
                  label="Comment (optional)"
                  fullWidth multiline rows={2} margin="dense"
                  value={actionComment}
                  onChange={(e) => setActionComment(e.target.value)}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button
              onClick={() => setActionDialog({ open: false, request: null, role: '' })}
              sx={{ color: '#888' }}
            >
              Cancel
            </Button>
            <Button
              variant="outlined" color="error"
              startIcon={<CancelIcon />}
              onClick={() => handleAction('reject')}
              sx={{ borderRadius: 2 }}
            >
              Reject
            </Button>
            <Button
              variant="contained"
              startIcon={<CheckCircleIcon />}
              onClick={() => handleAction('approve')}
              sx={{ background: '#1a4d2e', '&:hover': { background: '#2d7a4f' }, borderRadius: 2 }}
            >
              {actionDialog.role === 'admin' ? 'Final Approve' : 'Approve'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}